import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';

import { useConnectionRecordsQuery } from '@/hooks/api/queries/useConnectionRecordsQuery';
import { usePagesQuery } from '@/hooks/api/queries/usePagesQuery';
import { ResizableCollapsiblePanel } from '@/components/layout/ResizableCollapsiblePanel';
import { NavigateAwayModal } from '@/components/NavigateAwayModal';
import { usePagesPageContext } from '@/pages/pages/PagesPageContext';
import { SettingsPanel } from '@/pages/pages/settings-panel/SettingsPanel';
import { ROUTES } from '@/Router';
import { AddViewModal } from './add-view/AddViewModal';
import { PageEditorMessagingContextProvider } from './MessagingContext';
import { PageEditorContextProvider, usePageEditorContext } from './PageEditorContext';
import { PageEditorLiveAppFrame } from './PageEditorLiveAppFrame';
import { PageEditorTopBar } from './PageEditorTopBar';
import { PageEmptyState } from './PageEmptyState';

function PageEditorContent() {
  const { setSettingsPanelItem } = usePagesPageContext();
  const { page, isPageModified } = usePageEditorContext();
  const [t] = useTranslation();

  // Update the settings panel item when the active page changes
  useEffect(() => {
    setSettingsPanelItem({ type: 'page', page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.key, setSettingsPanelItem]);

  return (
    <>
      <div className="flex h-full flex-col">
        <PageEditorTopBar />
        <div className="flex flex-1">
          <ResizableCollapsiblePanel
            panelContent={<SettingsPanel />}
            mainContent={<PageEditorLiveAppFrame />}
            autoSaveId="settings-panel"
            position="right"
            title={t('pages.element_settings.heading')}
          />
        </div>
      </div>
      <AddViewModal />
      <NavigateAwayModal condition={isPageModified} />
    </>
  );
}

export function PageEditor({ isIndex = false }: { isIndex?: boolean }) {
  const urlParams = useParams<{ id: string }>();
  const { data: pages, isPending: isPagesQueryPending } = usePagesQuery();

  const { id: pageKey } = urlParams;
  const page = pages?.find((p) => p.key === pageKey);

  const { data: sourceObjectRecords } = useConnectionRecordsQuery({
    objectKey: page?.sourceObjectKey || '',
    enabled: !!page?.sourceObjectKey
  });

  const emptyStateWrapperClasses = 'flex flex-1 items-center justify-center';

  if (isPagesQueryPending) {
    // TODO: create loading skeleton for the page editor
    return null;
  }

  if (!pages) {
    throw new Error('Error fetching pages');
  }

  if (isIndex) {
    if (pages.length === 0) {
      return (
        <div className={emptyStateWrapperClasses}>
          <PageEmptyState emptyStateKey="no-pages" />
        </div>
      );
    }
    return (
      <div className={emptyStateWrapperClasses}>
        <PageEmptyState emptyStateKey="select-page" />
      </div>
    );
  }

  if (!page) {
    return <Navigate to={ROUTES.PAGES} replace />;
  }

  return (
    <PageEditorMessagingContextProvider>
      <PageEditorContextProvider initialPage={page} pageSourceObjectRecords={sourceObjectRecords}>
        <PageEditorContent />
      </PageEditorContextProvider>
    </PageEditorMessagingContextProvider>
  );
}
